/**
 * @typedef {Object} ObjectTransformConfigEntry
 * @property {string|string[]} [to] - The target property or properties.
 * @property {any[]} [default] - The default value (optional).
 * @property {function(any): any} [transform] - A transformation function to apply to the value (optional).
 */

/**
 * @typedef {Object.<string, ObjectTransformConfigEntry>} ObjectTransformConfig
 */

/**
 * Transforms a source object based on a configuration object.
 * @param {ObjectTransformConfig} config - The configuration object that defines the transformation rules.
 * @param {Object} source_object - The source object to be transformed.
 * @returns {Object} - The transformed target object.
 */
export function object_transform(config, source_object) {
    const target = {};
    if (!source_object) {
        return target;
    }

    // transform the source object to the target object
    for (const [key, value] of Object.entries(source_object)) {
        if (!config[key]) {
            continue;
        }

        const config_item = config[key];

        const target_key = config_item.to ?? key;
        let target_value = value ?? config_item.default;
        if (!['string', 'number'].includes(typeof target_value) && target_value) {
            if (!Array.isArray(target_value)) {
                target_value = Object.entries(target_value)
                    .map(([k, v]) => (v ? k : undefined))
                    .filter(Boolean);
            }
        }

        if (typeof config_item.transform === 'function') {
            config_item.transform(target_value, target_key, target);
        } else {
            target[target_key] = target_value;
        }
    }

    return target;
}
